import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MesFinancementsService } from '@features/mes-financements/services/mes-finacements.service';
import { catchError, concat, EMPTY, map, of, switchMap, withLatestFrom } from 'rxjs';
import { MesFinancementsActions } from '@features/mes-financements/store/financements/mes-financements.actions';
import { Store } from '@ngrx/store';
import { selectAllDispositifs, selectAllSirets } from '@features/referentiel/features';
import { MesFinancementsFunctions } from '@features/mes-financements/features/mes-financements-recherche/functions/mes-financements-fonctions';
import { selectFilteredAndLimitedEntitesSiretFromQuery, selectRechercheFormCategorie, selectRechercheFormValues } from '@features/mes-financements/store/search/search.selectors';
import { MesFinancementSearchFormActions } from '@features/mes-financements/store/search/search.actions';
import { ToastService } from '@shared/services/toast/toast.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { mesFinancementsFeature } from '../financements/mes-financements.reducers';
import { createExportEffectFactory } from '@shared/functions/common/export-effects.utils';

function buildSearchCriteria(
  form: any,
  sirets: string[],
  categorieDossier: any
) {
  const siretForm = form.siret ? [form.siret] : sirets;
  return MesFinancementsFunctions.searchFormToDossierSearchCriteria(
    siretForm,
    form,
    categorieDossier
  );
}

function fetchDossiers(
  dossierSearchCriteria: any,
  mesFinancementsServices: MesFinancementsService
) {
  return concat(
    of(MesFinancementsActions.updateLoading({ isLoading: true })),
    mesFinancementsServices.getDossiers(dossierSearchCriteria).pipe(
      map((dossiers) =>
        MesFinancementsActions.getDossiersBySearchCriteriaSuccess({ dossiers })
      ),
      catchError((error) =>
        of(
          MesFinancementsActions.getDossiersBySearchCriteriaFailure({
            errorMessage: error.error,
          })
        )
      )
    ),
    of(MesFinancementsActions.updateLoading({ isLoading: false }))
  );
}

export const loadDossiers$ = createEffect(
  (
    actions$ = inject(Actions),
    mesFinancementsServices = inject(MesFinancementsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesFinancementSearchFormActions.updateSearchForm),
      withLatestFrom(store.select(selectAllSirets), store.select(selectRechercheFormCategorie)),
      switchMap(([action, sirets, categorieDossier]) => {
        const criteria = buildSearchCriteria(
          action.form,
          sirets,
          categorieDossier
        );
        return sirets.length > 0
          ? fetchDossiers(criteria, mesFinancementsServices)
          : EMPTY;
      })
    );
  },
  { functional: true },
);

export const resetDossiers$ = createEffect(
  (
    actions$ = inject(Actions),
    mesFinancementsServices = inject(MesFinancementsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesFinancementSearchFormActions.resetSearchForm, MesFinancementSearchFormActions.getSearchForm),
      withLatestFrom(
        store.select(selectRechercheFormValues),
        store.select(selectAllSirets),
        store.select(selectRechercheFormCategorie),
      ),
      switchMap(([, form, sirets, categorieDossier]) => {
        const criteria = buildSearchCriteria(form, sirets, categorieDossier);
        return sirets.length > 0
          ? fetchDossiers(criteria, mesFinancementsServices)
          : EMPTY;
      })
    );
  },
  { functional: true }
);

export const searchOrganismeFormationFromQuerySiret$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesFinancementSearchFormActions.searchSiretFromQuery),
      switchMap(() =>
        store.select(selectFilteredAndLimitedEntitesSiretFromQuery(10)).pipe(
          map(result => {
            if (result.length === 0) {
              return MesFinancementSearchFormActions.searchSiretFromQueryFailure({ validationErrors: { siret: { notFound: true } } });
            }
            return MesFinancementSearchFormActions.searchSiretFromQuerySucces();
          })
        )
      )
    )
  },
  { functional: true },
);

export const exportDossiersFinancement$ = createExportEffectFactory({
  trigger: MesFinancementSearchFormActions.exportFinancements,
  success: MesFinancementSearchFormActions.exportFinancementsSucces,
  failure: MesFinancementSearchFormActions.exportFinancementsFailure,
  selectors: () => {
    const store = inject(Store);
    return {
      sirets$: store.select(selectAllSirets),
      form$: store.select(selectRechercheFormValues),
      categorie$: store.select(selectRechercheFormCategorie),
      dispositifs$: store.select(selectAllDispositifs),
      total$: store.select(mesFinancementsFeature.selectTotalElements),
    };
  },
  criteriaBuilder: ({ sirets, form, categorieDossier, dispositifs }) =>
    MesFinancementsFunctions.searchFormToFinancementRechercheExportRequest(sirets, form, categorieDossier, dispositifs),
  call: (svc, criteria) => svc.getDossiersExport(criteria),
  injectService: () => inject(MesFinancementsService),
  loading: {
    start: MesFinancementsActions.updateExportLoading,
    stop: MesFinancementsActions.updateExportLoading,
  },
});

export const exportStagiairesFinancement$ = createExportEffectFactory({
  trigger: MesFinancementSearchFormActions.exportInterns,
  success: MesFinancementSearchFormActions.exportInternsSucces,
  failure: MesFinancementSearchFormActions.exportInternsFailure,
  selectors: () => {
    const store = inject(Store);
    return {
      sirets$: store.select(selectAllSirets),
      form$: store.select(selectRechercheFormValues),
      categorie$: store.select(selectRechercheFormCategorie),
      dispositifs$: store.select(selectAllDispositifs),
      total$: store.select(mesFinancementsFeature.selectTotalElements),
    };
  },
  criteriaBuilder: ({ sirets, form, categorieDossier, dispositifs }) =>
    MesFinancementsFunctions.searchFormToFinancementRechercheExportRequest(sirets, form, categorieDossier, dispositifs),
  call: (svc, criteria) => svc.getStagiairesExport(criteria),
  injectService: () => inject(MesFinancementsService),
  loading: {
    start: MesFinancementsActions.updateExportLoading,
    stop: MesFinancementsActions.updateExportLoading,
  },
});

export const showExportErrorToast$ = createEffect(
  (
    actions$ = inject(Actions),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(MesFinancementSearchFormActions.exportFinancementsFailure, MesFinancementSearchFormActions.exportInternsFailure),
      map((action) => {
        toastService.showErrorToastWithButtonAndWithoutTitle(action.error ?? translateService.instant('toast.export.error'));
        return MesFinancementsActions.noAction();
      })
    );
  },
  { functional: true },
);
